import React, { useEffect, useState } from 'react';
import { useAdmin } from '../admin/AdminContext';
import {
    Box,
    Container,
    AppBar,
    Toolbar,
    Typography,
    Button,
    Tabs,
    Tab,
    useMediaQuery,
} from '@mui/material';
import {
    Add as AddIcon,
    Assignment as AssignmentIcon,
    Star as StarIcon,
    Logout as LogoutIcon,
    Description as DescriptionIcon,
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Import components
import CreateLinksTab from '../components/admin/CreateLinksTab';
import LinkDetailsTab from '../components/admin/LinkDetailsTab';
import InterviewResultsTab from '../components/admin/InterviewResultsTab';
import RatingsTab from '../components/admin/RatingsTab';

// Custom theme
const theme = createTheme({
    palette: {
        primary: {
            main: '#667eea',
            light: '#8b9cf5',
            dark: '#4a5fc7',
        },
        secondary: {
            main: '#764ba2',
            light: '#9a6fc4',
            dark: '#5a3580',
        },
        background: {
            default: '#f8fafc',
            paper: '#ffffff',
        },
    },
    typography: {
        fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
        h4: { fontWeight: 700 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600 },
    },
    shape: { borderRadius: 12 },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 600,
                    borderRadius: 8,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                    borderRadius: 16,
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    fontWeight: 500,
                    minHeight: 48,
                },
            },
        },
    },
});

export default function AdminDashboard() {
    const { fetchRatings, fetchCandidates, logout } = useAdmin();
    const [activeTab, setActiveTab] = useState(0);
    const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(null);
    const [items, setItems] = useState<any[]>([]);
    const [candidates, setCandidates] = useState<any[]>([]);
    const [totals, setTotals] = useState({ total: 0, rated: 0, feedback: 0 });
    const [daysRange, setDaysRange] = useState(7);

    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const load = async () => {
        // Fetch ratings
        const r = await fetchRatings();
        const newItems = r.items || [];
        const newTotals = r.totals || { total: 0, rated: 0, feedback: 0 };
        setItems(prev => JSON.stringify(prev) === JSON.stringify(newItems) ? prev : newItems);
        setTotals(prev => JSON.stringify(prev) === JSON.stringify(newTotals) ? prev : newTotals);

        // Fetch candidates
        const c = await fetchCandidates();
        const newCandidates = c.candidates || [];
        setCandidates(prev => JSON.stringify(prev) === JSON.stringify(newCandidates) ? prev : newCandidates);
    };

    useEffect(() => { load(); }, []);

    useEffect(() => {
        const iv = setInterval(load, 10000);
        return () => clearInterval(iv);
    }, []);

    // Calculate chart data
    const dayMs = 24 * 60 * 60 * 1000;
    const today = new Date();

    const buckets: { label: string; start: number; end: number; ratings: number[] }[] = [];
    for (let i = daysRange - 1; i >= 0; i--) {
        const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
        const start = d.setHours(0, 0, 0, 0);
        const end = start + dayMs - 1;
        const label = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
        buckets.push({ label, start, end, ratings: [] });
    }

    items.forEach((it) => {
        const ts = it.lastFeedbackAtMs || it.lastStartAtMs || it.updatedAt || it._createdAt || it.createdAt;
        const t = typeof ts === 'number' ? ts : (ts && typeof ts.toDate === 'function' ? ts.toDate().getTime() : (ts ? new Date(ts).getTime() : 0));
        if (!t) return;
        for (const b of buckets) {
            if (t >= b.start && t <= b.end) {
                if (typeof it.rating === 'number' && it.rating > 0) b.ratings.push(it.rating);
                break;
            }
        }
    });

    const chartPoints = buckets.map((b) => {
        const avg = b.ratings.length ? b.ratings.reduce((s, v) => s + v, 0) / b.ratings.length : 0;
        return { label: b.label, value: avg };
    });

    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
                {/* Header */}
                <AppBar
                    position="static"
                    elevation={0}
                    sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                >
                    <Toolbar sx={{ py: 2 }}>
                        <Container maxWidth="lg" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: { xs: 0 } }}>
                            <Box>
                                <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
                                    Interview Admin
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                                    Manage interviews and candidate feedback
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                onClick={() => { logout(); window.location.href = '/admin'; }}
                                startIcon={<LogoutIcon />}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.2)',
                                    backdropFilter: 'blur(10px)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                }}
                            >
                                {!isMobile && 'Logout'}
                            </Button>
                        </Container>
                    </Toolbar>

                    {/* Navigation Tabs */}
                    <Container maxWidth="lg" sx={{ px: { xs: 0 } }}>
                        <Tabs
                            value={activeTab}
                            onChange={(_, v) => {
                                setActiveTab(v);
                                setSelectedCandidateId(null);
                            }}
                            variant={isMobile ? 'fullWidth' : 'standard'}
                            sx={{
                                '& .MuiTab-root': {
                                    color: 'rgba(255,255,255,0.7)',
                                    '&.Mui-selected': { color: 'white' },
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: 'white',
                                    height: 3,
                                    borderRadius: '3px 3px 0 0',
                                },
                            }}
                        >
                            <Tab icon={<AddIcon />} iconPosition="start" label={!isMobile ? "Create Links" : undefined} sx={{ minHeight: 56 }} />
                            <Tab icon={<AssignmentIcon />} iconPosition="start" label={!isMobile ? "Link Details" : undefined} sx={{ minHeight: 56 }} />
                            <Tab icon={<DescriptionIcon />} iconPosition="start" label={!isMobile ? "Interview Results" : undefined} sx={{ minHeight: 56 }} />
                            <Tab icon={<StarIcon />} iconPosition="start" label={!isMobile ? "Ratings" : undefined} sx={{ minHeight: 56 }} />
                        </Tabs>
                    </Container>
                </AppBar>

                {/* Content */}
                <Container maxWidth="lg" sx={{ py: 4 }}>
                    {activeTab === 0 && <CreateLinksTab onSuccess={load} />}
                    {activeTab === 1 && <LinkDetailsTab linkItems={candidates.length ? candidates : items} onNavigateToResults={(candidateId) => { setSelectedCandidateId(candidateId); setActiveTab(2); }} />}
                    {activeTab === 2 && <InterviewResultsTab candidates={candidates} selectedCandidateId={selectedCandidateId} />}
                    {activeTab === 3 && (
                        <RatingsTab
                            items={items}
                            totals={totals}
                            chartPoints={chartPoints}
                            daysRange={daysRange}
                            setDaysRange={setDaysRange}
                        />
                    )}
                </Container>
            </Box>
        </ThemeProvider>
    );
}
